import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, MapPin, Phone, Building2, Home as HomeIcon } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../context/languagecontext';
import { useAudience } from '../context/audiencecontext';

export const Hero = () => {
  const { t } = useLanguage();
  const { audienceType, isHomeowner, isCommercial } = useAudience();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1, y: 0,
      transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
    }
  };

  const getCustomTitle = () => {
    if (isHomeowner) return "Turn Your Dark Basement Into a Bright, Legal Living Space";
    if (isCommercial) return "Professional Concrete Cutting Solutions for Commercial Projects";
    return t('hero.title');
  };

  const getCustomSubtitle = () => {
    if (isHomeowner) return "Expert egress window installation and basement renovations in Edmonton";
    if (isCommercial) return "Trusted partner for contractors, property managers, and developers across Alberta";
    return t('hero.subtitle');
  };

  const getCustomFeatures = () => {
    if (isCommercial) return [t('hero.commercialFeature1'), t('hero.commercialFeature2'), t('hero.commercialFeature3'), t('hero.commercialFeature4')];
    return [t('hero.feature1'), t('hero.feature2'), t('hero.feature3'), t('hero.feature4')];
  };
  
  return (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <motion.img
          key={audienceType}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          src={isCommercial
            ? "https://images.unsplash.com/photo-1685464196339-46a985b2049b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            : "https://images.unsplash.com/photo-1766961980272-921bba4240bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          }
          alt="Concrete Cutting"
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${isCommercial ? 'bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/85' : 'bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/80'}`}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div className="max-w-3xl" variants={containerVariants} initial="hidden" animate="visible">
          <motion.div variants={itemVariants} className="inline-flex items-center space-x-2 bg-orange-500/10 backdrop-blur-sm border border-orange-500/20 rounded-sm px-4 py-2 mb-6">
            {audienceType && (
              <>
                {isHomeowner ? <HomeIcon className="w-4 h-4 text-orange-500" /> : <Building2 className="w-4 h-4 text-orange-500" />}
                <span className="text-sm font-bold uppercase tracking-wider text-orange-500 mr-2">{isHomeowner ? 'Homeowner' : 'Commercial'}</span>
                <span className="text-slate-400">|</span>
              </>
            )}
            <MapPin className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-bold uppercase tracking-wider text-orange-500">{t('hero.location')}</span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight uppercase tracking-wide">
            {getCustomTitle()}
          </motion.h1>
          
          <motion.p variants={itemVariants} className="font-body text-xl md:text-2xl text-gray-300 mb-6 leading-relaxed">
            {getCustomSubtitle()}
          </motion.p>

          <motion.p variants={itemVariants} className="font-body text-lg text-gray-300 mb-8">
            {t('hero.description')}
          </motion.p>

          {isCommercial && (
            <motion.div variants={itemVariants} className="inline-flex items-center space-x-4 bg-orange-500 rounded-sm px-6 py-4 mb-8 shadow-hard">
              <div className="flex items-center space-x-2">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="font-heading text-white font-bold text-xl uppercase tracking-wider">ANY SIZE • ANY COMPLEXITY</span>
              </div>
            </motion.div>
          )}

          <motion.div variants={itemVariants} className="space-y-3 mb-10">
            <h3 className="font-heading text-white font-bold text-lg mb-4 uppercase tracking-wider">
              {isCommercial ? t('hero.whyTrustCommercial') : t('hero.whyTrust')}
            </h3>
            {getCustomFeatures().map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 + (index * 0.1), duration: 0.5 }} className="flex items-start space-x-3 group">
                <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="font-body text-gray-300 leading-relaxed">{feature}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mb-6">
            <Link to="/contact" className="group inline-flex items-center justify-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider rounded-sm shadow-hard transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
              {t('hero.ctaPrimary')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href={`tel:${companyInfo.phone}`} className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-bold uppercase tracking-wider rounded-sm border border-white/20 transition-all hover:-translate-y-1">
              <Phone className="mr-2 w-5 h-5" />
              {companyInfo.phone}
            </a>
          </motion.div>

          <motion.div variants={itemVariants} className="flex items-center text-gray-400 text-sm font-body">
            <span className="mr-2">{t('hero.alsoAvailable')}</span>
            <a href={`tel:${companyInfo.phoneSecondary}`} className="text-orange-500 hover:text-orange-400 font-bold transition-colors uppercase tracking-wider">
              {companyInfo.phoneSecondary}
            </a>
          </motion.div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.5 }}>
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-sm flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-sm"></div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
